import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useBrand } from "@/context/BrandContext";
import defaultLogo from "@/assets/skoda-logo.png";
import onedealerLogo from "@/assets/onedealer-logo.webp";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import client, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sun, Moon, Monitor } from "lucide-react";

const SSO_ERROR_KEYS = {
  sso_no_account: "login.ssoNoAccount",
  sso_account_deactivated: "login.ssoAccountDeactivated",
  sso_failed: "login.ssoFailed",
};

export default function Login() {
  const { login, status } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoChecked, setSsoChecked] = useState(false);

  useEffect(() => {
    if (status === "authenticated") {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [status, navigate, location.state]);

  useEffect(() => {
    client
      .get("/auth/sso/status")
      .then((r) => setSsoEnabled(!!r.data?.enabled))
      .catch(() => setSsoEnabled(false))
      .finally(() => setSsoChecked(true));
  }, []);

  // The password form is only shown with /login?showlogin=true. Without SSO
  // configured it is the only way in, so it is shown regardless.
  const showLoginForm =
    new URLSearchParams(location.search).get("showlogin") === "true" || (ssoChecked && !ssoEnabled);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("error");
    const key = code && SSO_ERROR_KEYS[code];
    if (key) setError(t(key));
  }, [location.search, t]);

  const ssoLogin = () => {
    const from = location.state?.from?.pathname || "/";
    window.location.href = `${API}/auth/sso/login?next=${encodeURIComponent(from)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await login(email.trim(), password);
      if (user?.must_change_password) {
        toast.warning("Please change your temporary password", { duration: 5000 });
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || t("login.invalid"));
    } finally {
      setBusy(false);
    }
  };

  const cycleTheme = () => {
    const order = ["light", "dark", "system"];
    const idx = order.indexOf(theme);
    setTheme(order[(idx + 1) % order.length]);
  };

  return (
    <div className="grid min-h-screen w-full grid-cols-1 lg:grid-cols-5">
      {/* Left decorative pane */}
      <div className="relative hidden overflow-hidden lg:col-span-2 lg:flex" data-testid="login-brand-pane">
        <div className="absolute inset-0 bg-secondary/50" />
        <div className="absolute inset-0 opacity-40 map-placeholder" />
        <div className="relative z-10 flex h-full w-full flex-col justify-between p-10">
          <div className="flex flex-col items-start gap-1.5">
            <img
              src={brand.brand_logo_url || defaultLogo}
              alt={brand.brand_name}
              className="h-9 w-auto shrink-0 rounded-sm object-contain"
            />
            <p className="text-base font-semibold leading-tight">{brand.brand_name}</p>
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              SAD Dealer Network Data Platform
            </h1>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              by OneDealer NetSites
            </p>
          </div>
          <div />
        </div>
      </div>

      {/* Right form pane */}
      <div className="col-span-1 flex items-center justify-center p-6 lg:col-span-3">
        <div className="w-full max-w-md">
          <div className="mb-4 flex items-center justify-between gap-1.5 lg:mb-6 lg:justify-end">
            {/* Compact brand header, mobile/tablet only (the left pane is hidden there) */}
            <div className="flex min-w-0 items-center gap-2 lg:hidden" data-testid="login-brand-compact">
              <img
                src={brand.brand_logo_url || defaultLogo}
                alt={brand.brand_name}
                className="h-7 w-auto shrink-0 rounded-sm object-contain"
              />
              <p className="truncate text-sm font-semibold leading-tight">{brand.brand_name}</p>
            </div>
            <div className="flex items-center gap-1.5">
            <div
              className="flex items-center rounded-md border border-border p-0.5 font-mono text-[11px]"
              data-testid="login-lang-switch"
            >
              {["en", "de"].map((l) => (
                <button
                  key={l}
                  data-testid={`login-lang-btn-${l}`}
                  onClick={() => setLang(l)}
                  className={`rounded px-2 py-1 uppercase tracking-widest transition-colors ${
                    lang === l ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <Button data-testid="login-theme-btn" variant="ghost" size="icon" onClick={cycleTheme} aria-label="Theme">
              {theme === "dark" ? <Moon className="h-4 w-4" /> : theme === "light" ? <Sun className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
            </Button>
            </div>
          </div>

          <div className="mb-10 lg:hidden" data-testid="login-title-compact">
            <h1 className="text-xl font-bold leading-tight tracking-tight">SAD Dealer Network Data Platform</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">by OneDealer NetSites</p>
          </div>

          {!showLoginForm && ssoChecked && (
            <div className="space-y-4">
              {error && (
                <p data-testid="login-error" className="text-xs font-medium text-destructive">
                  {error}
                </p>
              )}
              <Button data-testid="login-enter-platform" type="button" className="w-full" onClick={ssoLogin}>
                {t("login.enterPlatform")}
              </Button>
            </div>
          )}

          {showLoginForm && (
          <Card className="border-border p-8 shadow-sm">
            <div className="mb-8">
              <p className="data-label mb-2">{t("login.subtitle")}</p>
              <h2 className="text-3xl font-bold tracking-tight">{t("login.title")}</h2>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="data-label">
                  {t("login.email")}
                </Label>
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="data-label">
                  {t("login.password")}
                </Label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    data-testid="login-forgot-password-link"
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t("login.forgotPassword")}
                  </Link>
                </div>
              </div>
              {error && (
                <p data-testid="login-error" className="text-xs font-medium text-destructive">
                  {error}
                </p>
              )}
              <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full">
                {busy ? t("common.loading") : t("login.submit")}
              </Button>
              {ssoEnabled && (
                <Button
                  data-testid="login-sso-submit"
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={ssoLogin}
                >
                  {t("login.ssoSubmit")}
                </Button>
              )}
            </form>
          </Card>
          )}

          <a
            href="https://www.onedealer.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="login-powered-by"
            className="mb-6 mt-20 flex flex-col items-center justify-center gap-1 text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <span>{t("login.poweredBy")}</span>
            <img src={onedealerLogo} alt="OneDealer" className="h-12 w-auto" />
          </a>
        </div>
      </div>
    </div>
  );
}
